import { useForm } from '@mantine/form';
import { useEffect, useRef, useState } from 'react';
import {
  useCreateEventAlert,
  useDeleteEventAlert,
  useStepEventAlerts,
  useUpdateEventAlert,
} from '../../../../../../api/events-client/events-client.hooks';
import type { EventAlert } from '../../../../../../api/events-client/events-client.type';
import { type Mapping, type Step, type Workflow } from '../../../../../../api/workflows-client/workflows-client.type';
import { useUpdateWorkflowStepMappings } from '../../../../../../api/workflows-client/workflows.client.hooks';

type FormValues = {
  mappings: Mapping[];
  event_alerts: EventAlert[];
};

const DEFAULT_ALERT_VALUES: EventAlert = {
  id: '',
  step_id: '',
  event_model: 'task',
  raw_event_type: '',
  step_mapping_relationship: 'includes',
  step_mapping_names: [],
};

const useWorkflowStepSettings = (
  step: Step | null,
  workflow: Workflow | null,
  onClose: () => void,
  onConfirm: (updatedStep: Step) => void,
) => {
  const [isAddingAlert, setIsAddingAlert] = useState(false);
  const [isEditingAlert, setIsEditingAlert] = useState(false);
  const previousStepId = useRef<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newAlertValues, setNewAlertValues] = useState<EventAlert>(DEFAULT_ALERT_VALUES);

  // Event alert hooks
  const { createEventAlert, isCreating } = useCreateEventAlert();
  const { deleteEventAlert, isDeleting } = useDeleteEventAlert();
  const { updateEventAlert, isUpdating: isUpdatingAlert } = useUpdateEventAlert();
  const { data: stepEventAlerts, query: eventAlertsQuery } = useStepEventAlerts(step?.id);

  // Workflow step mappings update hook
  const { mutate: updateWorkflowStepMappings } = useUpdateWorkflowStepMappings({
    onSuccess: () => {
      setIsUpdating(false);
    },
  });

  // Initialize form with step data
  const form = useForm<FormValues>({
    initialValues: {
      mappings: [],
      event_alerts: [],
    },
  });

  // Function to check if there are unsaved important status changes
  const hasUnsavedImportantChanges = () => {
    if (!step) return false;

    return form.values.mappings.some((mapping) => {
      const originalMapping = step.mappings.find((m) => m.id === mapping.id);
      return originalMapping && originalMapping.is_important !== mapping.is_important;
    });
  };

  const handleOnClose = (ignoreUnsavedChanges = false) => {
    // Check if there are unsaved changes to important statuses
    if (hasUnsavedImportantChanges() && !ignoreUnsavedChanges) {
      const shouldClose = window.confirm(
        'You have unsaved changes to statuses. Are you sure you want to close without saving?',
      );
      if (!shouldClose) {
        return; // Stay in the modal if the user cancels
      }
    }

    // Proceed with closing the modal
    onClose();
    setIsAddingAlert(false);
    setIsEditingAlert(false);
    setNewAlertValues(DEFAULT_ALERT_VALUES);
  };

  // Update form when modal is opened with step data
  useEffect(() => {
    if (step) {
      // Always update form when modal is opened
      // Create deep copies of mappings and event_alerts to avoid reference issues
      const mappingsCopy = step.mappings.map((mapping) => ({ ...mapping }));

      // Use stepEventAlerts from the hook if available, otherwise use empty array
      const eventAlertsCopy = stepEventAlerts ? stepEventAlerts.map((alert) => ({ ...alert })) : [];

      form.setValues({
        mappings: mappingsCopy,
        event_alerts: eventAlertsCopy,
      });

      // Update the ref to track the current step id
      previousStepId.current = step.id;
    } else {
      // Reset form if no step data
      form.reset();
      previousStepId.current = null;
    }
  }, [step, stepEventAlerts, form.setValues, form.reset]);

  const handleSubmit = async (values: FormValues) => {
    if (!step || !workflow) return;

    // Check if there are any changes to the is_important fields
    const hasImportantFieldChanges = values.mappings.some((mapping) => {
      const originalMapping = step.mappings.find((m) => m.id === mapping.id);
      return originalMapping && originalMapping.is_important !== mapping.is_important;
    });

    // Only proceed with update if there are changes to is_important fields
    if (!hasImportantFieldChanges) {
      handleOnClose();
      return;
    }

    setIsUpdating(true);

    // Create an updated step with the new mapping is_important values
    const updatedStep: Step = {
      ...step,
      mappings: values.mappings.map((mapping) => ({
        ...mapping,
        order_index: step.mappings.find((m) => m.id === mapping.id)?.order_index || 0,
      })),
    };

    // Use updateWorkflowStepMappings instead of updating the entire workflow
    updateWorkflowStepMappings(
      {
        workflowId: workflow.id,
        mappings: updatedStep.mappings,
      },
      {
        onSuccess: () => {
          // Call onConfirm to update local state in parent component
          onConfirm(updatedStep);
          handleOnClose(true);
        },
        onError: (error) => {
          console.error('Error updating workflow step mappings:', error);
          setIsUpdating(false);
          // Handle error appropriately - possibly show a notification
        },
      },
    );
  };

  const handleAddAlert = async () => {
    if (!newAlertValues.raw_event_type || newAlertValues.step_mapping_names.length === 0 || !step) return;

    try {
      // Create the event alert on the backend
      const createdAlert = await createEventAlert({
        ...newAlertValues,
        step_id: step.id,
      });

      // Add the created alert to the form
      form.setFieldValue('event_alerts', [...form.values.event_alerts, createdAlert]);

      // Reset the new alert values
      setNewAlertValues({
        id: '',
        step_id: step.id,
        event_model: 'task',
        raw_event_type: '',
        step_mapping_relationship: 'includes',
        step_mapping_names: [],
      });

      setIsAddingAlert(false);
    } catch (error) {
      console.error('Error creating event alert:', error);
      // You might want to show an error notification here
    }
  };

  const handleEditAlert = (alert: EventAlert) => {
    setNewAlertValues({
      ...alert,
    });
    setIsEditingAlert(true);
  };

  const handleUpdateAlert = async () => {
    if (!newAlertValues.raw_event_type || newAlertValues.step_mapping_names.length === 0 || !step) return;

    try {
      // Update the event alert on the backend
      const updatedAlert = await updateEventAlert({
        ...newAlertValues,
      });

      // Update the alert in the form
      const updatedAlerts = form.values.event_alerts.map((alert) =>
        alert.id === updatedAlert.id ? updatedAlert : alert,
      );
      form.setFieldValue('event_alerts', updatedAlerts);

      // Reset edit state
      setNewAlertValues({
        id: '',
        step_id: step.id,
        event_model: 'task',
        raw_event_type: '',
        step_mapping_relationship: 'includes',
        step_mapping_names: [],
      });
      setIsEditingAlert(false);
    } catch (error) {
      console.error('Error updating event alert:', error);
      // You might want to show an error notification here
    }
  };

  const handleCancelEdit = () => {
    setIsEditingAlert(false);
    setNewAlertValues({
      id: '',
      step_id: step?.id || '',
      event_model: 'task',
      raw_event_type: '',
      step_mapping_relationship: 'includes',
      step_mapping_names: [],
    });
  };

  const handleRemoveAlert = async (alertId: string) => {
    try {
      // Delete the event alert from the backend
      await deleteEventAlert(alertId);

      // Remove the alert from the form
      form.setFieldValue(
        'event_alerts',
        form.values.event_alerts.filter((alert) => alert.id !== alertId),
      );
    } catch (error) {
      console.error('Error deleting event alert:', error);
      // You might want to show an error notification here
    }
  };

  return {
    form,
    isAddingAlert,
    setIsAddingAlert,
    isEditingAlert,
    setIsEditingAlert,
    isUpdating,
    newAlertValues,
    setNewAlertValues,
    isCreating,
    isDeleting,
    isUpdatingAlert,
    stepEventAlerts,
    eventAlertsQuery,
    handleOnClose,
    handleSubmit,
    handleAddAlert,
    handleEditAlert,
    handleUpdateAlert,
    handleCancelEdit,
    handleRemoveAlert,
  };
};

export { DEFAULT_ALERT_VALUES, useWorkflowStepSettings };
